import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import TriggerActionService from "../../components/TriggerActionService";
import ActionTriggerConstants from "../../components/ActionTriggerConstants";
import UnityBridge from "./utils/UnityBridge";
import ModalManager from "./utils/ModalManager";
import { getBuildName, getBuildVersion, setupTokens, stripParamFromUrl } from "./utils/UrlParams";
import { allowedPaymentOrigin, mustRedirectPayments, redirect } from "./utils/PaymentsUtils";
import Localize from "../../components/Localize/Localize";
import LocalizeKeys from "../../components/Localize/LocalizeKeys";
import InstallBanner from "../../components/pwa/InstallBanner";
import "./Game.css";
import cookieManager from "../../components/CookieManager";
import UserAgent from "../../components/Utils/UserAgent";
import Constants from "../../components/Constants";
import GeoLocationHelper from "../../components/Utils/GeoLocationHelper";
import { detectAdblock } from "../../components/adblock";

const Game = () => {
    const [isLandscape, setIsLandscape] = useState(false);
    const [dimensions, setDimensions] = useState({
        height: window.innerHeight,
        width: (window.innerHeight * 16) / 9,
    });
    const [lobbyReady, setLobbyReady] = useState(false);
    const [activeModal, setActiveModal] = useState(null);
    const [activeConfirmationModal, setActiveConfirmationModal] = useState(null);
    const [activeInfoModal, setActiveInfoModal] = useState(null);
    const [checkoutOverlayUrl, setCheckoutOverlayUrl] = useState(null);
    const [activeCheckoutOverlay, setActiveCheckoutOverlay] = useState(false);
    const [preInitDone, setPreInitDone] = useState(false);
    const [buildValid, setBuildValid] = useState(null); // null=checking, true=ok, false=invalid

    const buildName = getBuildName();
    
    // The landing forwards its device id as ?device_id=... Persist it and strip it
    // from the URL so it does not linger in the address bar / browser history.
    // Not consent-gated: see CookieManager.getDeviceId.
    useEffect(() => {
        const deviceIdFromUrl = new URLSearchParams(window.location.search).get("device_id");
        if (!deviceIdFromUrl) return;
        cookieManager.set(Constants.DEVICE_ID, deviceIdFromUrl);
        stripParamFromUrl("device_id");
    }, []);
    
    const fitContain = (vw, vh, targetRatio) => {
        const viewportRatio = vw / vh;
        if (viewportRatio > targetRatio) {
            const height = vh;
            const width = Math.round(height * targetRatio);
            return { width, height };
        } else {
            const width = vw;
            const height = Math.round(width / targetRatio);
            return { width, height };
        }
    };

    const calculateDimensions = (landscapeFlag) => {
        const vw = Math.round(window.innerWidth);
        const vh = Math.round(window.innerHeight);

        if (!landscapeFlag) {
            // PORTRAIT: on mobile fill screen; desktop use 3:4 contained box
            if (UserAgent.isMobile() && vw < vh) {
                return { width: vw, height: vh };
            }
            return fitContain(vw, vh, 3 / 4);
        } else {
            // LANDSCAPE: on mobile fill screen; desktop use 16:9
            if (UserAgent.isMobile()) {
                const deviceLandscapeRatio = Math.max(vw, vh) / Math.min(vw, vh);
                return fitContain(vw, vh, deviceLandscapeRatio);
            }
            return fitContain(vw, vh, 16 / 9);
        }
    };

    // PRE-INIT
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                // Build version
                const buildVersion = getBuildVersion(buildName);
                cookieManager.set(Constants.BUILD_VERSION, buildVersion);

                // Country
                await GeoLocationHelper.resolveCountryByIP("Game");
                
                // Tokens
                await setupTokens();

                // Ad-block detection is telemetry only: it no longer blocks the game,
                // so it runs in the background instead of delaying startup. A blocker
                // and a declined consent both cost attribution, never access.
                detectAdblock().then((adBlocked) => {
                    if (!adBlocked) return;
                    TriggerActionService.sendTriggerAction(
                      ActionTriggerConstants.AD_BLOCKER_DETECTED,
                      "",
                      { sourceUrl: "Game" }
                    ).catch((e) => {
                        console.warn("[-] TriggerActionService failed", e);
                    });
                });

                if (mounted) setPreInitDone(true);
            } catch (e) {
                console.warn("[Game] preInit failed:", e);
                if (mounted) setPreInitDone(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    // RESIZE EVENTS
    useEffect(() => {
        TriggerActionService.sendTriggerAction(
            ActionTriggerConstants.LANDING_OPEN,
            "",
            { sourceUrl: "Game" } // We know if it's the standalone PWA or not: UserAgent.isStandalone()
        ).catch((e) => console.warn("[-] TriggerActionService failed", e));

        const handleResize = () => setDimensions(calculateDimensions(isLandscape));

        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", handleResize);
        }
        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", handleResize);
            }
            window.removeEventListener("resize", handleResize);
        };
    }, [isLandscape]);

    // HANDLE MESSAGES
    useEffect(() => {
        const handleMessage = (event) => {
            // SECURE
            if (!(event.origin.includes("frontend.assets.tangelogames.app") ||
                allowedPaymentOrigin(event.origin) ||
                event.origin === window.origin)) {
                return;
            }

            const { action, modalType, idTask, extraInfo, value } = event.data || {};

            if (action === "onLobbyLoaded") {
                console.log("[Game] onLobbyLoaded");

                setLobbyReady(true);
            }

            if (action === "openFormModal") {
                let parsedExtra = {};
                try {
                    parsedExtra = typeof extraInfo === "string" && extraInfo ? JSON.parse(extraInfo) : {};
                    console.log("[Game] openFormModal received:", {action, modalType, parsedExtra});
                } catch (err) {
                    console.warn("[Game] Failed to parse extraInfo JSON:", err);
                }
                ModalManager.openModal(modalType, idTask, setActiveModal, parsedExtra);
            } else if (action === "setOrientation") {
                const newIsLandscape = value === "landscape";
                console.log("[Game] setOrientation received:", {value});
                setIsLandscape(newIsLandscape);
                setDimensions(calculateDimensions(newIsLandscape));
            } else if (action === "setExitDialog") {
                const enableDialog = value === "enable";
                console.log("[Game] setExitDialog received:", {value});
                if (enableDialog) {
                    addBeforeUnload();
                } else {
                    removeBeforeUnload();
                }
            } else if (action === "openUrl") {
                console.log("[Game] openUrl received:", {value});
                if (mustRedirectPayments()){
                    redirect(value);
                } else {
                    openCheckoutOverlay(value);
                }
            } else if (action === "closeCheckoutIframe") {
                console.log("[Game] closeCheckoutIframe received:");
                closeCheckoutOverlay(true);                
            } else {
                // Delegate the rest to UnityBridge
                console.log("[Game] postMessage received", {action, eventData:event.data});
                UnityBridge.handleAction(action, event.data);
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    // BUILD VALIDATION
    useEffect(() => {
        if (!buildName) return;
        const url = `https://frontend.assets.tangelogames.app/games/cash/webgl/${buildName}/index.html`;
        fetch(url, { method: 'HEAD' })
            .then(res => {
                if (res.ok) {
                    setBuildValid(true);
                } else {
                    console.error(`[Game] Build "${buildName}" not found on server (${res.status})`);
                    setBuildValid(false);
                }
            })
            .catch(err => {
                console.error('[Game] Build validation failed:', err);
                setBuildValid(false);
            });
    }, []);

    // Show bottom hint only on mobile portrait while the game is in landscape mode
    const showMobileLandscapeHint =
        UserAgent.isMobile() && window.innerWidth < window.innerHeight && isLandscape;
    
    let beforeUnloadRegistered = false;
    
    (function pushHistoryStates(){
      if (!window.history.state || !window.history.state._mundiHistory) {
          window.history.pushState({_mundiHistory: true}, '', window.location.href);
      }
    })();

    const addBeforeUnload = () => {
        if (!beforeUnloadRegistered) {
            window.addEventListener("beforeunload", handleBeforeUnload);
            beforeUnloadRegistered = true;
        }
    };

    const removeBeforeUnload = () => {
        if (beforeUnloadRegistered) {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            beforeUnloadRegistered = false;
        }
    };

    // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#examples
    const handleBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = true; // legacy support
    };
  
    // https://stackoverflow.com/a/49719812
    window.addEventListener('popstate', function(event) {
        ModalManager.openModal("exitConfirmation", "confirmation-id", setActiveConfirmationModal, "");
    });

    const openCheckoutOverlay = (url) => {
        setCheckoutOverlayUrl(url);
        setActiveCheckoutOverlay(true);
    }
    
    const closeCheckoutOverlay = (postToIframe) => {
        setActiveCheckoutOverlay(false);
        
        if (postToIframe) {
            UnityBridge.handleAction("closeCheckoutIframe", "");
        }

        const gameFrame = document.getElementById("game-iframe");
        gameFrame.focus();
    }
    
    if (!buildName) {
        console.error("[Game] No build name resolved — redirecting to 404");
        return <Navigate replace to="/404" />;
    }

    if (buildValid === false) {
        console.error(`[Game] Build "${buildName}" does not exist — redirecting to 404`);
        return <Navigate replace to="/404" />;
    }

    const indexUrl = `https://frontend.assets.tangelogames.app/games/cash/webgl/${buildName}/index.html`;

    return (
        <div>
        { preInitDone && lobbyReady && <InstallBanner /> }
        <div id="game-container" className="game-container">
            <div id="iframe-wrapper" className="iframe-wrapper">

                {/* Game iframe */}
                {preInitDone && buildValid === true && (
                    <iframe
                        id="game-iframe"
                        className="game-iframe"
                        src={indexUrl}
                        allowFullScreen
                        style={{
                            width: `${dimensions.width}px`,
                            height: `${dimensions.height}px`,
                        }}
                        onLoad={() => {
                            console.log("[Game.js] Game iframe loaded");
                            TriggerActionService.sendTriggerAction(
                                ActionTriggerConstants.UNITY_LOAD_STARTED,
                                "",
                                { sourceUrl: "Game" }
                            ).catch((e) => console.warn("[-] TriggerActionService failed", e));
                        }}
                    />
                )}

                {/* Modal */}
                {activeCheckoutOverlay && (
                    <div
                        className="checkout-iframe-wrapper"
                    >
                        <div onClick={(e) => e.stopPropagation()}>
                            <div
                                className="iframe-container"
                                style={{
                                    position: "relative",
                                    width: `${dimensions.width}px`,
                                    height: `${dimensions.height}px`
                                }}
                            >
                                <img
                                    className="checkout-iframe-button"
                                    src="/assets/back.png"
                                    alt={Localize.translateToUpper("LANDING_BUTTON_BACK")}
                                    onClick={() => closeCheckoutOverlay(true)}
                                />

                                <iframe
                                    src={checkoutOverlayUrl}
                                    allow="payment"
                                    title="Mundigames Cash Checkout"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        border: "none",
                                        borderRadius: "12px",
                                        backgroundColor: "white",
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Bottom overlay hint */}
                {showMobileLandscapeHint && (
                    <div className="mobile-landscape-hint">
                    <img
                            src="/assets/ICON_Rotate.png"
                            alt="Rotate"
                            aria-hidden="true"
                            className="mobile-landscape-hint__icon"
                        />
                        <span className="mobile-landscape-hint__text">
                          {Localize.translate(LocalizeKeys.ROTATE_PHONE)}
                        </span>
                    </div>
                )}
                
                {/* Show modal */}
                {activeInfoModal && ModalManager.renderModal(activeInfoModal, setActiveInfoModal)}
                {activeModal && ModalManager.renderModal(activeModal, setActiveModal)}
                {/* Show confirmation modal */}
                {activeConfirmationModal && ModalManager.renderModal(activeConfirmationModal, setActiveConfirmationModal)}
            </div>
        </div>
        </div>
    );
};

export default Game;
